/**
 * The catalogue of guided tours, and a live table of which of them can be
 * started right now.
 *
 * Tours are owned by the component that owns the state they narrate — SubNav
 * holds the tab tours, Settings its own, AddEditInventory the vehicle form, and
 * so on — because every tour hook takes that component's live state as
 * arguments (useSettingsTour needs selectedMenuItem, useReportTour needs
 * hasGeneratedReport). Hoisting them all into Cars.jsx to give the header
 * something to call would mean threading all of that state upwards.
 *
 * So owners register a start function here on mount, and HelpMenu reads the
 * table. Deliberately an external store rather than an emitter event: the menu
 * has to render "the tours you can start on this screen" BEFORE anything is
 * clicked, and a fire-and-forget channel cannot answer that question — it can
 * only fail silently after the fact.
 */
//Static catalogue. `scope` decides how HelpMenu groups the entry:
//  global — always offered, from anywhere
//  tab    — belongs to a tab, so it can be reached by navigating there first
//
//There is deliberately no "modal" scope. Every modal in the app draws a
//full-screen overlay, so while one is open the header — and therefore the help
//menu — cannot be clicked at all. A modal's tour has to be started from inside
//the modal, which is what the vehicle form, the composer, the column dialog,
//the export dialog and the security screen each do with their own help button.
//
//DECLARED IN TAB-STRIP ORDER. HelpMenu renders "Other screens" by iterating
//this object, so the menu reads down the app the same way the tab strip does.
//
//No permission is recorded here. A tour's permission is whatever its tab
//requires, so callers ask tabPermissions.js — the same map NavTabs gates the
//tabs with. Two earlier versions of this file kept a copy: first a hand-written
//`perm:` per tour, then a `perm` derived from tabPermission() at module load.
//The derived one was worse, because it looked safe. It handed HelpMenu an
//any-of ARRAY that HelpMenu passed to `can(perm)` as a single argument, and
//`can` takes a rest list — so `can(["messages.send"])` asked whether the user
//held a permission literally named `["messages.send"]`. False, forever. Every
//permission-gated tour vanished from the menu and only Inventory and Sales were
//ever offered. Ask canSeeTab(tour.tab, can); do not reintroduce a `perm` field.
const TOUR_DEFS = {
    overview:   { label: "App overview",      tab: null,              scope: "global" },
    dashboard:  { label: "Dashboard tour",    tab: "tab-dashboard",   scope: "tab" },
    inventory:  { label: "Inventory tour",    tab: "tab-inventory",   scope: "tab" },
    sales:      { label: "Sales tour",        tab: "tab-sales",       scope: "tab" },
    customers:  { label: "Customers tour",    tab: "tab-customers",   scope: "tab" },
    brokers:    { label: "Brokers tour",      tab: "tab-brokers",     scope: "tab" },
    sellers:    { label: "Sellers tour",      tab: "tab-sellers",     scope: "tab" },
    messages:   { label: "Messages tour",     tab: "tab-messages",    scope: "tab" },
    enquiries:  { label: "Enquiries tour",    tab: "tab-enquiries",   scope: "tab" },
    payments:   { label: "Payments tour",     tab: "tab-payments",    scope: "tab" },
    reports:    { label: "Reports tour",      tab: "tab-reports",     scope: "tab" },
    settings:   { label: "Settings tour",     tab: "tab-settings",    scope: "tab" },
    recyclebin: { label: "Recycle Bin tour",  tab: "tab-recyclebin",  scope: "tab" },
};

export const TOURS = Object.freeze(Object.fromEntries(
    Object.entries(TOUR_DEFS).map(([id, def]) => [id, Object.freeze({ ...def })])
));

const slots = new Map();        //tour id -> start function
const listeners = new Set();
let snapshot = [];              //stable identity between changes, for useSyncExternalStore

const emit = () => { for (const l of listeners) l(); };

//Rebuilt only on a real change: useSyncExternalStore compares by identity, so
//handing it a fresh array every render would loop.
const rebuild = () => { snapshot = [...slots.keys()]; emit(); };

export function registerTour(id, start) {
    slots.set(id, start);
    rebuild();
    flushPending(id);
}

export function unregisterTour(id, start) {
    //SubNav swaps whole subtrees when the tab changes, and React can mount the
    //replacement before unmounting the old one. Compare identity so the
    //departing owner cannot delete its successor's slot.
    if (slots.get(id) !== start) return;
    slots.delete(id);
    rebuild();
}

export function startRegisteredTour(id) {
    const start = slots.get(id);
    if (!start) return false;
    start();
    return true;
}

//Starting a tour whose owner lives on another tab: the caller navigates, we
//arm the id, and registerTour fires it as soon as the owner mounts.
let pending = null;
let pendingTimer = null;

export function requestTour(id) {
    if (startRegisteredTour(id)) return;
    pending = id;
    clearTimeout(pendingTimer);
    //If the owner never arrives (navigation blocked by an unsaved-changes
    //guard, say), forget rather than ambushing the user later.
    pendingTimer = setTimeout(() => { pending = null; }, 8000);
}

function flushPending(id) {
    if (pending !== id) return;
    pending = null;
    clearTimeout(pendingTimer);
    //One frame, so the owner's subtree has painted before its own
    //handleStartTutorial starts probing the DOM for step targets.
    requestAnimationFrame(() => startRegisteredTour(id));
}

export const subscribeTours = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
export const availableTours = () => snapshot;
